/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from 'react';
import './DropdownList.scss';
import PropTypes from 'prop-types';
import { appAlert, appKeyCode } from '@utils/globalConstant';
import { Notification } from '@core/Notification';
import Icons from '@core/Utils/Icons/Icons';

export function DropdownList({
  id = 'id',
  name = 'label',
  label = '',
  isRequired = false,
  value: valueProp = '',
  placeholder = '',
    // BFSG: default options props are temporary
  options = [
    {
      id: 0,
      label: 'Option 1'
    },
    {
      id: 1,
      label: 'Option 2'
    }
  ],
  hint = '',
  valid = false,
  invalid = false,
  validMessage = '',
  invalidMessage = '',
  onChange = () => {},
  ...restProps
}) {
  const dropdownRef = useRef(null);
  const itemsRef = useRef([]);
  const listRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [selectedId, setSelectedId] = useState(valueProp);
  const [selectedVal, setSelectedVal] = useState(null);
  const [lastKeyPressed, setLastKeyPressed] = useState(null); // Track the last pressed key
  const [searchIndex, setSearchIndex] = useState(-1); // To track the index within the matched options
  const [searchLetter, setSearchLetter] = useState(''); // To store the typed letter

  const toggleDropdown = (e) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    if (!highlightedIndex) setHighlightedIndex(0);
  };

  // Function to find the next item that matches the letter
  const findNextMatch = (startIndex, letter) => {
    for (let i = startIndex; i < options.length; i += 1) {
      // Replaced ++ with i += 1
      if (options[i][name]?.toLowerCase()?.startsWith(letter)) {
        return i;
      }
    }
    return -1;
  };

  const handleKeyDown = (event) => {
    const { key, keyCode } = event;
    const items = itemsRef.current.filter(Boolean); // Remove undefined refs
    if (!items.length) return;
    if (/^[a-zA-Z]$/.test(key)) {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      }
      if (key === lastKeyPressed) {
        // If the same letter is pressed consecutively, find the next matching option

        setSearchIndex((prev) => {
          const nextIndex = findNextMatch(prev + 1, key.toLowerCase()); // Find next match
          const updatedIndex = nextIndex !== -1 ? nextIndex : findNextMatch(0, key.toLowerCase());

          // You can use the updatedIndex value here as needed
          setHighlightedIndex(updatedIndex); // Highlight the matched option
          return updatedIndex; // This is the value that will be stored in the state
        });
      } else {
        // Reset searchIndex and set the new letter
        setSearchLetter(key.toLowerCase());
        setLastKeyPressed(key.toLowerCase());
        const newHiglightedIndex = findNextMatch(0, key.toLowerCase());
        setSearchIndex(newHiglightedIndex);
        setHighlightedIndex(newHiglightedIndex);
      }
    } else {
      switch (keyCode) {
        case appKeyCode.ESCAPE:
          closeDropdown();
          break;

        case appKeyCode.ENTER:
        case appKeyCode.SPACE: {
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else if (highlightedIndex !== -1) {
            const selectedOption = options[highlightedIndex];
            setSelectedId(selectedOption.id);
            setSelectedVal(selectedOption.label);
            onChange(selectedOption.id);
            closeDropdown();
          }
          break;
        }

        case appKeyCode.ARROW_DOWN:
        case appKeyCode.ARROW_UP: {
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else if (keyCode === appKeyCode.ARROW_DOWN) {
            setHighlightedIndex((prev) => Math.min(prev + 1, options.length - 1));
          } else {
            setHighlightedIndex((prev) => Math.max(prev - 1, 0));
          }
          break;
        }

        case appKeyCode.HOME:
        case appKeyCode.END: {
          event.preventDefault();
          const selectedOption = keyCode === appKeyCode.HOME ? 0 : options.length - 1;
          if (!isOpen) {
            setIsOpen(true);
            setHighlightedIndex(selectedOption);
          } else if (highlightedIndex !== -1) {
            setHighlightedIndex(selectedOption);
          }
          break;
        }

        case appKeyCode.TAB:
          if (isOpen && highlightedIndex !== -1) {
            const selectedOption = options[highlightedIndex];
            setSelectedId(selectedOption.id);
            setSelectedVal(selectedOption.label);
            onChange(selectedOption.id);
            closeDropdown();
            setHighlightedIndex(0);
          }
          break;

        default:
          break;
      }
    }
  };

  useEffect(() => {
    if (isOpen && highlightedIndex !== -1 && itemsRef.current[highlightedIndex]) {
      const item = itemsRef.current[highlightedIndex];
      item.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest' // scroll the item into view if it's not fully visible
      });
    }
  }, [highlightedIndex, isOpen]);

  // useEffect(() => {
  //   if (valueProp) {
  //     options.find((option) => {
  //       if (option.id === valueProp) {
  //         setSelectedVal(option.label);
  //         return true;
  //       }
  //       return false;
  //     });
  //   }
  // }, []);
  useEffect(() => {
    const initialId = valueProp;
    if (initialId) {
      options.find((option) => {
        if (option.id === initialId) {
          setSelectedVal(option.label);
          return true;
        }
        return false;
      });
    }
  }, [valueProp, options]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="dropdown" ref={dropdownRef}>
      <label htmlFor={id} className="form-label" id={`${id}-label`}>
        {label}
        {!isRequired && (
          <span className="ek-labeltext-12-medium text-ek-blue-placeholder ps-4">(optional)</span>
        )}
      </label>
      {/* BFSG: add proper aria-labels */}
      <div
        className="d-flex justify-content-between align-items-center form-control space-48 w-100 border border-secondary border-top-0 border-end-0 border-start-0 border-start-0 btn-dropdown-toggle"
        role="combobox"
        tabIndex={0}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown} 
        aria-describedby={restProps['aria-describedby']}
        name={name}
        value={options[selectedId]?.label ? `${id}-label` : ''}
        id={id}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="dropdown-list"
        aria-activedescendant={isOpen ? `option-${highlightedIndex}` : undefined}
        >
        {selectedVal ? (
          <span className="text-ek-copy-16-regular">{selectedVal}</span>
        ) : (
          selectedVal ? <span> {selectedVal} </span> : <span className="ek-placeholder-text text-ek-blue-placeholder">{placeholder}</span>
        )}
        <Icons name={isOpen ? 'ARROW_UP' : 'ARROW_DOWN'} colorType='dark' />
      </div>

      <div
        className={`dropdown-menu bg-ek-white rounded-4 py-10 px-12 mt-4 z-1 position-absolute w-100 ${
          isOpen ? 'show' : ''
        }`}>
        <div
          className="bg-transparent overflow-x-hidden overflow-y-scroll w-100 border-0 position-relative pe-24"
          style={{ height: '212px' }}
          role="listbox"
          id="dropdown-list">
            <ul ref={listRef}>
              {options.length > 0 &&
                options.map((option, index) => (
                  <li
                    key={index}
                    ref={(el) => (itemsRef.current[index] = el)}
                    type="button"
                    onClick={() => {
                      onChange(option.id);
                      setSelectedId(option.id);
                      setSelectedVal(option.label);
                      closeDropdown();
                    }}
                    className={`dropdown-item w-100 rounded-3 px-16 m-0 mb-4 
                      ${
                        index === highlightedIndex && highlightedIndex !== selectedId
                          ? 'border border-ek-blue-placeholder'
                          : 'border border-ek-white'
                      }
                      ${option.id === selectedId ? 'bg-secondary' : 'bg-transparent'}`}
                    style={{
                      paddingTop: '3px',
                      paddingBottom: '3px',
                      whiteSpace: 'normal',
                      overflow: 'visible',
                      wordWrap: 'break-word'
                    }}
                    onMouseOver={() => setHighlightedIndex(index)}
                    onFocus={() => setHighlightedIndex(index)}
                    role="option"
                    id={`option-${index}`}
                    aria-selected={selectedVal === option.label}
                  >
                    {option.label}
                  </li>
                ))}
            </ul>
        </div>
      </div>

      <div id={restProps['aria-describedby']}>
        {hint && !valid && !invalid && (
          <div id={`${id}-hint`} className="form-text pt-4 ps-4" aria-live="polite">
            <Notification message={hint} />
          </div>
        )}
        {invalid && invalidMessage && (
          <div id={`${id}-error`} className="form-text pt-4 ps-4" aria-live="polite">
            <Notification type={appAlert.ERROR} message={invalidMessage} />
          </div>
        )}
        {valid && validMessage && (
          <div id={`${id}-success`} className="form-text pt-4 ps-4" aria-live="polite">
            <Notification type={appAlert.SUCCESS} message={validMessage} />
          </div>
        )}
      </div>
    </div>
  );
}

DropdownList.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  value: PropTypes.any,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, label: PropTypes.string.isRequired })
  ),
  onChange: PropTypes.func
};

export default DropdownList;
